/**
 * Pre-queue setup state machine store (B08 — pre-queue-setup-devices).
 *
 * Implements the `PrequeueStateMachine` from `openspec/changes/pre-queue-setup-devices/specs/pre-queue-setup`:
 * - 11 discrete states with guarded transitions.
 * - Invalid transitions throw in dev (`import.meta.env.DEV`), no-op in prod.
 * - Every transition appends to `history` (ring buffer cap = `PREQUEUE_HISTORY_RING_SIZE`).
 * - Slots for ready-token, preview stream, mobile facingMode, selected device IDs.
 *
 * The store is intentionally UI-agnostic: components read state + call `transition`,
 * hooks/effects mutate the slots. Tests assert table-driven transition validity.
 */
import {
  PREQUEUE_HISTORY_RING_SIZE,
  type PrequeueEvent,
  type PrequeueReadyToken,
  type PrequeueState,
  type WebcamAction,
} from '@shared/prequeue';
import { create } from 'zustand';

// --- Transition table -------------------------------------------------------

/**
 * Allowed transitions per state. Source → set of valid destinations.
 *
 * `virtual-camera-blocked` is reachable from any pre-`ready` state because the
 * validation hook in `PreQueueSetupPage` runs whenever `cameraLabel` resolves
 * (including via localStorage cache populated by `LivenessModal` earlier in
 * the auth flow). The label can match a blocklist entry before the user has
 * even clicked "Enable camera & microphone", so we must accept the early
 * transition rather than throw (bug 2026-05-17: "Invalid prequeue transition
 * idle → virtual-camera-blocked" on Continue-as-guest flow).
 *
 * Symmetric reverse paths from `virtual-camera-blocked` let the user resume
 * picking after they pick a non-virtual cam.
 */
const TRANSITIONS: Readonly<Record<PrequeueState, ReadonlyArray<PrequeueState>>> = {
  idle: ['requesting-permission', 'virtual-camera-blocked'],
  'requesting-permission': ['permission-denied', 'enumerating', 'virtual-camera-blocked'],
  'permission-denied': ['requesting-permission', 'idle', 'virtual-camera-blocked'],
  enumerating: ['enumeration-failed', 'picking', 'virtual-camera-blocked'],
  'enumeration-failed': ['enumerating', 'idle', 'virtual-camera-blocked'],
  picking: ['validating', 'requesting-permission', 'picking', 'virtual-camera-blocked'],
  validating: ['virtual-camera-blocked', 'ready', 'picking'],
  'virtual-camera-blocked': ['picking', 'idle', 'requesting-permission', 'enumerating'],
  ready: ['enqueueing', 'picking', 'idle', 'virtual-camera-blocked'],
  enqueueing: ['enqueued', 'ready', 'idle'],
  enqueued: ['idle', 'ready'],
};

/** Pure helper exported for tests. */
export function isValidPrequeueTransition(from: PrequeueState, to: PrequeueState): boolean {
  const allowed = TRANSITIONS[from];
  return allowed !== undefined && allowed.includes(to);
}

// --- Store shape ------------------------------------------------------------

export type FacingMode = 'user' | 'environment';

interface PrequeueStoreState {
  // State machine
  state: PrequeueState;
  history: PrequeueEvent[];

  // Slots
  readyToken: PrequeueReadyToken | null;
  previewStream: MediaStream | null;
  facingMode: FacingMode;
  selectedCameraId: string | null;
  selectedMicId: string | null;
  cameraLabel: string | null;
  micLabel: string | null;

  // Virtual camera detection result (resolved by useWebcamValidation)
  virtualCameraDetected: boolean;
  virtualCameraAction: WebcamAction | null;

  // Actions
  transition: (to: PrequeueState, reason?: string) => void;
  setReadyToken: (token: PrequeueReadyToken | null) => void;
  setPreviewStream: (stream: MediaStream | null) => void;
  setFacingMode: (mode: FacingMode) => void;
  setSelectedCameraId: (id: string | null, label?: string | null) => void;
  setSelectedMicId: (id: string | null, label?: string | null) => void;
  setVirtualCameraResult: (detected: boolean, action: WebcamAction | null) => void;
  reset: () => void;
}

const INITIAL: Pick<
  PrequeueStoreState,
  | 'state'
  | 'history'
  | 'readyToken'
  | 'previewStream'
  | 'facingMode'
  | 'selectedCameraId'
  | 'selectedMicId'
  | 'cameraLabel'
  | 'micLabel'
  | 'virtualCameraDetected'
  | 'virtualCameraAction'
> = {
  state: 'idle',
  history: [],
  readyToken: null,
  previewStream: null,
  facingMode: 'user',
  selectedCameraId: null,
  selectedMicId: null,
  cameraLabel: null,
  micLabel: null,
  virtualCameraDetected: false,
  virtualCameraAction: null,
};

/**
 * Detect dev mode safely (vitest jsdom + Vite both expose `import.meta.env.DEV`).
 * Fallback `false` when running outside Vite (e.g., raw ESM, server-side).
 */
function isDev(): boolean {
  try {
    return Boolean((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    return false;
  }
}

export const usePrequeueStore = create<PrequeueStoreState>((set, get) => ({
  ...INITIAL,

  transition: (to, reason) => {
    const from = get().state;
    if (from === to) {
      // Idempotent self-transition: still record (helps debug picker reselection).
    } else if (!isValidPrequeueTransition(from, to)) {
      const msg = `Invalid prequeue transition ${from} → ${to}`;
      if (isDev()) {
        throw new Error(msg);
      }
      // prod: silent no-op
      return;
    }
    const ev: PrequeueEvent = {
      at: new Date().toISOString(),
      from,
      to,
      ...(reason !== undefined ? { reason } : {}),
    };
    const nextHistory = [...get().history, ev];
    if (nextHistory.length > PREQUEUE_HISTORY_RING_SIZE) {
      nextHistory.splice(0, nextHistory.length - PREQUEUE_HISTORY_RING_SIZE);
    }
    set({ state: to, history: nextHistory });
  },

  setReadyToken: (readyToken) => set({ readyToken }),

  setPreviewStream: (previewStream) => set({ previewStream }),

  setFacingMode: (facingMode) => set({ facingMode }),

  setSelectedCameraId: (selectedCameraId, label) =>
    set({
      selectedCameraId,
      ...(label !== undefined ? { cameraLabel: label } : {}),
    }),

  setSelectedMicId: (selectedMicId, label) =>
    set({
      selectedMicId,
      ...(label !== undefined ? { micLabel: label } : {}),
    }),

  setVirtualCameraResult: (virtualCameraDetected, virtualCameraAction) =>
    set({ virtualCameraDetected, virtualCameraAction }),

  reset: () => set({ ...INITIAL, history: [] }),
}));
